import { useEffect } from 'react';
import { SITE_BASE_URL } from '../../constants';

/**
 * Universal SEO & Social Meta Manager Component
 * 
 * Dynamically updates:
 * - document.title
 * - meta[name="description"]
 * - meta[name="keywords"]
 * - meta[name="robots"]
 * - link[rel="canonical"]
 * - OpenGraph meta tags (og:title, og:description, og:url, og:image, og:type, og:site_name)
 * - Twitter Card meta tags (twitter:card, twitter:title, twitter:description, twitter:image)
 * - JSON-LD Structured Data script tags in <head>
 */
const SEO = ({
  title,
  description = 'Transform daily Scripture reading into a joyful habit. Play timed Bible quizzes, compete in church & student leagues, win sponsored prizes, and grow in God’s Word.',
  keywords = 'Bible quiz, Bible contest, daily scripture reading, Christian trivia, Sunday school quiz, Bible competition, scripture memorization, church league, Christian youth app',
  canonicalUrl,
  ogImage = '/android-chrome-512x512.png',
  ogType = 'website',
  robots = 'index, follow',
  schema,
}) => {
  useEffect(() => {
    // 1. Page Title
    const formattedTitle = title
      ? title.includes('Bible Contest')
        ? title
        : `${title} | Bible Contest`
      : 'Bible Contest — Daily Scripture Reading Habit & Church Leagues';
    document.title = formattedTitle;

    // Helper to create or update meta tags
    const updateMetaTag = (attrName, attrVal, content) => {
      if (!content) return;
      let el = document.querySelector(`meta[${attrName}="${attrVal}"]`);
      if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attrName, attrVal);
        document.head.appendChild(el);
      }
      el.setAttribute('content', content);
    };

    // 2. Standard Meta Tags
    updateMetaTag('name', 'description', description);
    updateMetaTag('name', 'keywords', keywords);
    updateMetaTag('name', 'robots', robots);

    // 3. Canonical URL
    const resolvedCanonical = canonicalUrl 
      ? (canonicalUrl.startsWith('http') ? canonicalUrl : `${SITE_BASE_URL}${canonicalUrl}`)
      : `${SITE_BASE_URL}${window.location.pathname}`;

    let canonicalEl = document.querySelector('link[rel="canonical"]');
    if (!canonicalEl) {
      canonicalEl = document.createElement('link');
      canonicalEl.setAttribute('rel', 'canonical');
      document.head.appendChild(canonicalEl);
    }
    canonicalEl.setAttribute('href', resolvedCanonical);

    // 4. OpenGraph Meta Tags
    const resolvedOgImage = ogImage.startsWith('http') ? ogImage : `${SITE_BASE_URL}${ogImage}`;
    updateMetaTag('property', 'og:title', formattedTitle);
    updateMetaTag('property', 'og:description', description);
    updateMetaTag('property', 'og:url', resolvedCanonical);
    updateMetaTag('property', 'og:type', ogType);
    updateMetaTag('property', 'og:site_name', 'Bible Contest');
    updateMetaTag('property', 'og:image', resolvedOgImage);

    // 5. Twitter Card Meta Tags
    updateMetaTag('name', 'twitter:card', 'summary_large_image');
    updateMetaTag('name', 'twitter:title', formattedTitle);
    updateMetaTag('name', 'twitter:description', description);
    updateMetaTag('name', 'twitter:image', resolvedOgImage);

    // 6. JSON-LD Structured Data
    let scriptEl = document.getElementById('page-structured-data');
    if (schema) {
      if (!scriptEl) {
        scriptEl = document.createElement('script');
        scriptEl.id = 'page-structured-data';
        scriptEl.type = 'application/ld+json';
        document.head.appendChild(scriptEl);
      }
      scriptEl.textContent = JSON.stringify(schema);
    } else if (scriptEl) {
      scriptEl.remove();
    }

    // Cleanup on unmount (keep base title/meta fallback)
    return () => {
      const currentScript = document.getElementById('page-structured-data');
      if (currentScript) currentScript.remove();
    };
  }, [title, description, keywords, canonicalUrl, ogImage, ogType, robots, schema]);

  return null;
};

export default SEO;
